import { applyGermanize } from './germanize.js';
import { useInstallPrompt, useIsMobile } from './hooks.js';
import { accent, accentBorder, accentDim, bg, border, textDim, textMuted } from './tokens.js';

const { useState, useEffect } = React;

// ─── Landing Page ────────────────────────────────────────────────────────
export function LandingPage({ onSelectGroup, onAbout, onSelectPattern, onMein }) {
  const mobile = useIsMobile();
  const install = useInstallPrompt();
  const [iosHint, setIosHint] = useState(false);
  function pickRandom() {
    const ids = Object.keys(PATTERNS);
    if (!ids.length) return;
    const id = ids[Math.floor(Math.random() * ids.length)];
    if (onSelectPattern) onSelectPattern(id);
  }
  return (
    <main style={{
      minHeight: "100dvh", display: "flex", flexDirection: "column",
      background: bg, padding: "0 20px", boxSizing: "border-box"
    }}>
      {/* Hero — vertikal zentriert */}
      <div style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        width: "100%"
      }}>
        <div style={{
          maxWidth: 720, width: "100%", textAlign: "center",
          padding: "24px 0"
        }}>
          {/* Symbol */}
          <svg viewBox="0 0 200 200" width={mobile ? 96 : 120} height={mobile ? 96 : 120}
               aria-hidden="true" style={{display: "block", margin: mobile ? "0 auto 5px" : "0 auto 6px"}}>
            <circle cx="100" cy="100" r="80" fill="none" stroke={accent} strokeWidth="3"/>
            <circle cx="100" cy="100" r="50" fill="none" stroke={accent} strokeWidth="2" opacity="0.6"/>
            <circle cx="130" cy="100" r="10" fill={accent}/>
          </svg>

          <div style={{
            fontFamily: "'Raleway', system-ui, sans-serif",
            fontSize: mobile ? 10 : 11, fontWeight: 300,
            letterSpacing: "0.3em", textTransform: "uppercase",
            color: "#e8e6e3", textAlign: "center",
            marginRight: "-0.3em",
            marginBottom: mobile ? 18 : 24
          }}>
            Obscura
          </div>

          <h1 style={{
            color: "#e8e6e3", fontSize: mobile ? 30 : 42,
            fontWeight: 300, letterSpacing: 0.5, lineHeight: 1.15,
            margin: mobile ? "0 0 14px" : "0 0 20px"
          }}>
            {applyGermanize("Muster sehen, die verborgen wirken.")}
          </h1>

          <p style={{
            color: textMuted, fontFamily: "Georgia, serif",
            fontSize: mobile ? 15 : 17, lineHeight: 1.6,
            maxWidth: 680, margin: mobile ? "0 auto 10px" : "0 auto 12px"
          }}>
            {applyGermanize("Ein Muster, das ich erkenne und benennen kann, verliert seine Macht ueber mich. Obscura macht rhetorische Figuren sichtbar, erklaert Erkennungsmerkmale und zeigt unterschiedliche Moeglichkeiten, auf sie zu reagieren.")}
          </p>

          <p style={{
            color: textDim, fontFamily: "Georgia, serif",
            fontSize: mobile ? 14 : 16, lineHeight: 1.6,
            maxWidth: 680, margin: mobile ? "0 auto 20px" : "0 auto 26px",
            fontStyle: "italic"
          }}>
            {applyGermanize("Erkenne, unterscheide und reagiere auf die Muster, die Gespraeche und Beziehungen untertunneln.")}
          </p>

          {/* Gruppen-Buttons als gefuellte Block-Buttons */}
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 10,
            justifyContent: "center", marginBottom: 14
          }}>
            {Object.entries(GROUPS).sort((a,b) => a[1].order - b[1].order).map(([key, g]) => (
              <button key={key}
                      onClick={() => onSelectGroup(key)}
                      style={{
                        background: accent, border: "none", borderRadius: 8,
                        padding: "10px 20px", color: bg,
                        fontSize: 14, fontWeight: 500, cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "background 0.2s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#d4584c"}
                      onMouseLeave={e => e.currentTarget.style.background = accent}>
                {applyGermanize(g.label)}
              </button>
            ))}
          </div>

          {/* Zufaellig erkunden */}
          <div style={{display: "flex", justifyContent: "center", marginBottom: 18}}>
            <button onClick={pickRandom}
              style={{
                background: "transparent", border: `1px solid ${accentBorder}`,
                borderRadius: 100, padding: "8px 18px",
                color: "#e8e6e3", fontSize: 12,
                cursor: "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 7
              }}
              onMouseEnter={e => { e.currentTarget.style.background = accentDim; e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = accentBorder; }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0}}>
                <path d="M2 4h2l1 2M14 4h-2l-4 8H6M14 4l-2 2M14 4l-2-2" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12h2l4-8" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 10l2 2-2 2" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {applyGermanize("Zufaellig erkunden")}
            </button>
          </div>

          {/* Als App installieren */}
          {(install.canInstall || install.isIOS) && (
            <div style={{display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 18}}>
              <button onClick={() => { if (install.canInstall) install.promptInstall(); else setIosHint(v => !v); }}
                style={{
                  background: "transparent", border: `1px solid ${accentBorder}`,
                  borderRadius: 100, padding: "8px 18px",
                  color: "#e8e6e3", fontSize: 12,
                  cursor: "pointer", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 7
                }}
                onMouseEnter={e => { e.currentTarget.style.background = accentDim; e.currentTarget.style.borderColor = accent; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = accentBorder; }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0}}>
                  <path d="M8 2v7M8 9l3-3M8 9L5 6" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12.5h10" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {applyGermanize("Als App installieren")}
              </button>
              {install.isIOS && iosHint && (
                <div style={{marginTop: 10, fontSize: 12, color: textDim, fontFamily: "Georgia, serif", fontStyle: "italic", maxWidth: 320, lineHeight: 1.5}}>
                  {applyGermanize("Tippe unten auf Teilen und dann auf „Zum Home-Bildschirm“.")}
                </div>
              )}
            </div>
          )}

          <div style={{display: "flex", gap: 18, justifyContent: "center", flexWrap: "wrap"}}>
            {onMein && (
              <button onClick={onMein} style={{
                background: "none", border: "none", color: textMuted,
                cursor: "pointer", fontFamily: "inherit", fontSize: 13
              }}>
                {applyGermanize("Mein Bereich")}
              </button>
            )}
            <button onClick={onAbout} style={{
              background: "none", border: "none", color: textMuted,
              cursor: "pointer", fontFamily: "inherit", fontSize: 13
            }}>
              {applyGermanize("Ueber Obscura")}
            </button>
          </div>
        </div>
      </div>

      {/* Footer-Tagline */}
      <div style={{
        fontSize: mobile ? 10 : 11, color: textDim,
        letterSpacing: mobile ? "0.08em" : "0.15em",
        textAlign: "center", padding: "0 0 20px",
        fontStyle: "italic"
      }}>
        {applyGermanize("Obscura – Rhetorische Figuren entzaubern")}
      </div>
    </main>
  );
}
